import { useState, useRef, useCallback, useEffect } from "react";
import { Upload, X, Image, Check, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const auth = sessionStorage.getItem("adminAuth");
  if (!auth) return null;
  return { Authorization: `Basic ${auth}` };
};

export const ImageUploader = ({ onUpload, currentImage }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage || null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setPreview(currentImage || null);
  }, [currentImage]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragOut = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const uploadFile = async (file) => {
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a JPG, PNG, GIF, or WebP image");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const headers = getAuthHeaders();
      const response = await axios.post(`${API}/admin/upload`, formData, {
        headers: {
          ...headers,
          "Content-Type": "multipart/form-data",
        },
      });

      const imageUrl = response.data.url;
      setPreview(imageUrl);
      onUpload?.(imageUrl);
      toast.success("Image uploaded successfully!");
    } catch (error) {
      toast.error("Failed to upload image");
      console.error("Upload error:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      uploadFile(files[0]);
    }
  }, []);

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      uploadFile(files[0]);
    }
  };

  const clearImage = () => {
    setPreview(null);
    onUpload?.("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="w-full">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        className="hidden"
        data-testid="image-file-input"
      />

      {preview ? (
        <div className="relative">
          <img
            src={preview}
            alt="Preview"
            className="w-full h-48 object-cover rounded-lg border border-stone-200"
          />
          <div className="absolute top-2 right-2 flex gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="p-2 bg-white rounded-full shadow-md hover:bg-stone-50"
              title="Change image"
            >
              <Upload className="w-4 h-4 text-stone-600" />
            </button>
            <button
              type="button"
              onClick={clearImage}
              className="p-2 bg-white rounded-full shadow-md hover:bg-red-50"
              title="Remove image"
            >
              <X className="w-4 h-4 text-red-500" />
            </button>
          </div>
          <div className="absolute bottom-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
            <Check className="w-3 h-3" />
            Uploaded
          </div>
        </div>
      ) : (
        <div
          onDragEnter={handleDragIn}
          onDragLeave={handleDragOut}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          data-testid="image-dropzone"
          className={`w-full h-48 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-all ${
            isDragging
              ? "border-deep-moss bg-deep-moss/5"
              : "border-stone-300 hover:border-deep-moss hover:bg-stone-50"
          }`}
        >
          {uploading ? (
            <>
              <Loader2 className="w-10 h-10 text-deep-moss animate-spin mb-3" />
              <p className="font-sans text-sm text-stone-600">Uploading...</p>
            </>
          ) : (
            <>
              <div className="w-14 h-14 rounded-full bg-stone-100 flex items-center justify-center mb-3">
                <Image className="w-7 h-7 text-stone-400" />
              </div>
              <p className="font-sans text-sm text-stone-600 mb-1">
                <span className="text-deep-moss font-medium">Click to upload</span> or drag and drop
              </p>
              <p className="font-sans text-xs text-stone-400">
                JPG, PNG, GIF or WebP (max 10MB)
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const ImageGalleryPicker = ({ onSelect, selectedUrl }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploader, setShowUploader] = useState(false);

  const fetchImages = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/admin/images`, { headers });
      setImages(response.data);
    } catch (error) {
      console.error("Failed to fetch images:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  const handleUploadComplete = (url) => {
    fetchImages();
    onSelect?.(url);
    setShowUploader(false);
  };

  return (
    <div className="space-y-4">
      {showUploader ? (
        <div>
          <ImageUploader onUpload={handleUploadComplete} />
          <button
            type="button"
            onClick={() => setShowUploader(false)}
            className="mt-2 text-sm text-stone-500 hover:text-stone-700"
          >
            Cancel
          </button>
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={() => setShowUploader(true)}
            data-testid="upload-new-image-btn"
            className="w-full py-3 border-2 border-dashed border-stone-300 rounded-lg text-stone-600 hover:border-deep-moss hover:text-deep-moss transition-colors flex items-center justify-center gap-2"
          >
            <Upload className="w-4 h-4" />
            Upload New Image
          </button>

          {loading ? (
            <div className="text-center py-8 text-stone-500">Loading images...</div>
          ) : images.length > 0 ? (
            <div className="grid grid-cols-4 gap-2 max-h-64 overflow-y-auto">
              {images.map((image) => {
                const fullUrl = image.url;
                const isSelected = selectedUrl === fullUrl;
                return (
                  <button
                    key={image.filename}
                    type="button"
                    onClick={() => onSelect?.(fullUrl)}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                      isSelected ? "border-deep-moss ring-2 ring-deep-moss/20" : "border-transparent hover:border-stone-300"
                    }`}
                  >
                    <img
                      src={fullUrl}
                      alt={image.filename}
                      className="w-full h-full object-cover"
                    />
                    {isSelected && (
                      <div className="absolute inset-0 bg-deep-moss/20 flex items-center justify-center">
                        <Check className="w-6 h-6 text-white drop-shadow-lg" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-center py-4 text-stone-500 text-sm">
              No images uploaded yet
            </p>
          )}
        </>
      )}
    </div>
  );
};
